import React from "react";
import { Box, Typography, IconButton, Modal, alpha } from "@mui/material";
import { styled } from "@mui/material/styles";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import CloseIcon from "@mui/icons-material/Close";

// Specialized glassmorphic image container for carousel
export const GlassImageContainer = styled(Box)(({ theme }) => ({
  cursor: "pointer",
  flexGrow: 1,
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  overflow: "hidden",
  borderRadius: 16,
  background: `linear-gradient(135deg, ${alpha(
    theme.palette.primary.main,
    0.05
  )} 0%, ${alpha(theme.palette.primary.main, 0.02)} 100%)`,
  backdropFilter: "blur(10px)",
  border: `1px solid ${alpha(theme.palette.common.white, 0.15)}`,
  boxShadow: `0 8px 32px ${alpha(theme.palette.common.black, 0.1)}`,
  width: "100%",
  height: "100%",
  transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
  position: "relative",
  "&::before": {
    content: '""',
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    height: "1px",
    background: `linear-gradient(90deg, transparent, ${alpha(
      theme.palette.common.white,
      0.2
    )}, transparent)`,
  },
  "&:hover": {
    transform: "translateY(-2px)",
    background: `linear-gradient(135deg, ${alpha(
      theme.palette.primary.main,
      0.08
    )} 0%, ${alpha(theme.palette.primary.main, 0.04)} 100%)`,
    border: `1px solid ${alpha(theme.palette.primary.main, 0.25)}`,
    boxShadow: `0 12px 40px ${alpha(theme.palette.primary.main, 0.15)}`,
  },
}));

// Glassmorphic navigation buttons for carousel
export const GlassNavButton = styled(IconButton)(({ theme }) => ({
  background: `linear-gradient(135deg, ${alpha(
    theme.palette.primary.main,
    0.12
  )} 0%, ${alpha(theme.palette.primary.main, 0.06)} 100%)`,
  backdropFilter: "blur(10px)",
  border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
  borderRadius: 12,
  color: theme.palette.primary.main,
  width: 48,
  height: 48,
  transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
  position: "relative",
  overflow: "hidden",
  "&::before": {
    content: '""',
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: `linear-gradient(135deg, ${alpha(
      theme.palette.common.white,
      0.1
    )}, transparent)`,
    opacity: 0,
    transition: "opacity 0.3s ease",
  },
  "&:hover": {
    background: `linear-gradient(135deg, ${alpha(
      theme.palette.primary.main,
      0.18
    )} 0%, ${alpha(theme.palette.primary.main, 0.09)} 100%)`,
    border: `1px solid ${alpha(theme.palette.primary.main, 0.3)}`,
    transform: "translateY(-1px)",
    boxShadow: `0 4px 20px ${alpha(theme.palette.primary.main, 0.2)}`,
    "&::before": {
      opacity: 1,
    },
  },
  "&:disabled": {
    background: `linear-gradient(135deg, ${alpha(
      theme.palette.action.disabled,
      0.08
    )} 0%, ${alpha(theme.palette.action.disabled, 0.04)} 100%)`,
    border: `1px solid ${alpha(theme.palette.action.disabled, 0.12)}`,
    color: theme.palette.action.disabled,
    transform: "none",
    boxShadow: "none",
  },
}));

// Glassmorphic modal with enhanced backdrop
export const GlassModal = styled(Modal)(({ theme }) => ({
  "& .MuiBackdrop-root": {
    backgroundColor: alpha(theme.palette.common.black, 0.8),
    backdropFilter: "blur(8px)",
  },
}));

// Glassmorphic modal content container
export const GlassModalContent = styled(Box)(({ theme }) => ({
  outline: "none",
  background: `linear-gradient(135deg, ${alpha(
    theme.palette.background.paper,
    0.95
  )} 0%, ${alpha(theme.palette.background.paper, 0.85)} 100%)`,
  backdropFilter: "blur(30px)",
  border: `1px solid ${alpha(theme.palette.common.white, 0.15)}`,
  maxWidth: "90vw",
  maxHeight: "90vh",
  borderRadius: 20,
  overflow: "hidden",
  position: "relative",
  boxShadow: `0 20px 60px ${alpha(theme.palette.common.black, 0.3)}`,
  "&::before": {
    content: '""',
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    height: "1px",
    background: `linear-gradient(90deg, transparent, ${alpha(
      theme.palette.common.white,
      0.4
    )}, transparent)`,
    zIndex: 1,
  },
}));

// Glassmorphic modal navigation buttons
export const GlassModalNavButton = styled(IconButton)(({ theme }) => ({
  position: "absolute",
  zIndex: 10,
  background: `linear-gradient(135deg, ${alpha(
    theme.palette.common.black,
    0.4
  )} 0%, ${alpha(theme.palette.common.black, 0.2)} 100%)`,
  backdropFilter: "blur(10px)",
  border: `1px solid ${alpha(theme.palette.common.white, 0.2)}`,
  borderRadius: 12,
  color: theme.palette.common.white,
  width: 56,
  height: 56,
  transition: "all 0.3s ease",
  "&:hover": {
    background: `linear-gradient(135deg, ${alpha(
      theme.palette.common.black,
      0.6
    )} 0%, ${alpha(theme.palette.common.black, 0.4)} 100%)`,
    border: `1px solid ${alpha(theme.palette.common.white, 0.3)}`,
    transform: "scale(1.05)",
    boxShadow: `0 8px 25px ${alpha(theme.palette.common.black, 0.4)}`,
  },
}));

// Glassmorphic close button
export const GlassCloseButton = styled(IconButton)(({ theme }) => ({
  position: "absolute",
  top: 16,
  right: 16,
  zIndex: 15,
  background: `linear-gradient(135deg, ${alpha(
    theme.palette.common.black,
    0.4
  )} 0%, ${alpha(theme.palette.common.black, 0.2)} 100%)`,
  backdropFilter: "blur(10px)",
  border: `1px solid ${alpha(theme.palette.common.white, 0.2)}`,
  borderRadius: 10,
  color: theme.palette.common.white,
  width: 40,
  height: 40,
  transition: "all 0.3s ease",
  "&:hover": {
    background: `linear-gradient(135deg, ${alpha(
      theme.palette.error.main,
      0.8
    )} 0%, ${alpha(theme.palette.error.main, 0.6)} 100%)`,
    border: `1px solid ${alpha(theme.palette.error.main, 0.4)}`,
    transform: "scale(1.1)",
    boxShadow: `0 4px 15px ${alpha(theme.palette.error.main, 0.3)}`,
  },
}));

// Glassmorphic caption container for modal
export const GlassCaptionContainer = styled(Box)(({ theme }) => ({
  position: "absolute",
  bottom: 20,
  left: "50%",
  transform: "translateX(-50%)",
  background: `linear-gradient(135deg, ${alpha(
    theme.palette.common.black,
    0.6
  )} 0%, ${alpha(theme.palette.common.black, 0.4)} 100%)`,
  backdropFilter: "blur(15px)",
  border: `1px solid ${alpha(theme.palette.common.white, 0.15)}`,
  borderRadius: 12,
  padding: theme.spacing(1, 2),
  zIndex: 10,
  "& .MuiTypography-root": {
    color: theme.palette.common.white,
    fontWeight: 500,
    fontSize: "0.9rem",
  },
}));

// Glassmorphic counter text
export const GlassCounterText = styled(Typography)(({ theme }) => ({
  fontWeight: 500,
  fontSize: "0.85rem",
  color: alpha(theme.palette.text.primary, 0.8),
  textAlign: "center",
  background: `linear-gradient(135deg, ${alpha(
    theme.palette.primary.main,
    0.08
  )} 0%, ${alpha(theme.palette.primary.main, 0.04)} 100%)`,
  backdropFilter: "blur(8px)",
  border: `1px solid ${alpha(theme.palette.primary.main, 0.12)}`,
  borderRadius: 8,
  padding: theme.spacing(0.5, 1.5),
  marginTop: theme.spacing(1),
  display: "inline-block",
}));

// Photo carousel image component
export const PhotoCarouselImage = ({
  src,
  alt,
  currentIndex,
  onClick,
  maxWidth = "400px",
  maxHeight = "300px",
}) => (
  <img
    src={src}
    alt={alt}
    onClick={onClick}
    style={{
      maxWidth,
      maxHeight,
      width: "100%",
      height: "auto",
      objectFit: "contain",
      display: "block",
      borderRadius: "12px",
      cursor: "pointer",
    }}
    onError={(e) => {
      console.error(
        `PhotoCarousel: Error loading image source: ${src} for index ${currentIndex}`
      );
      e.target.src = "/missing.jpg";
    }}
  />
);

// Modal image component
export const ModalImage = ({ src, alt, onError }) => (
  <img
    src={src}
    alt={alt}
    style={{
      maxWidth: "100%",
      maxHeight: "100%",
      objectFit: "contain",
      borderRadius: "16px",
    }}
    onError={onError}
  />
);

// Navigation controls component
export const NavigationControls = ({
  showArrows,
  onPrevious,
  onNext,
  disabled,
  canNavigate,
}) => {
  if (!showArrows) return null;

  return (
    <>
      <GlassNavButton
        onClick={onPrevious}
        aria-label="previous"
        disabled={disabled || !canNavigate}
      >
        <ArrowBackIosNewIcon />
      </GlassNavButton>
      <GlassNavButton
        onClick={onNext}
        aria-label="next"
        disabled={disabled || !canNavigate}
      >
        <ArrowForwardIosIcon />
      </GlassNavButton>
    </>
  );
};

// Modal navigation controls
export const ModalNavigationControls = ({ onPrevious, onNext, onClose }) => (
  <>
    <GlassModalNavButton
      aria-label="previous"
      onClick={onPrevious}
      sx={{ left: 20 }}
    >
      <ArrowBackIosNewIcon fontSize="large" />
    </GlassModalNavButton>

    <GlassModalNavButton aria-label="next" onClick={onNext} sx={{ right: 20 }}>
      <ArrowForwardIosIcon fontSize="large" />
    </GlassModalNavButton>

    <GlassCloseButton aria-label="close" onClick={onClose}>
      <CloseIcon fontSize="small" />
    </GlassCloseButton>
  </>
);

// Counter display component
export const PhotoCounter = ({ current, total, showInModal = false }) => {
  const content = `Photo ${current + 1} of ${total}`;

  if (showInModal) {
    return (
      <GlassCaptionContainer>
        <Typography>{content}</Typography>
      </GlassCaptionContainer>
    );
  }

  return (
    <Box display="flex" justifyContent="center">
      <GlassCounterText>{content}</GlassCounterText>
    </Box>
  );
};
